'use client';

import { FC, useMemo, ReactNode } from 'react';

import classNames from 'classnames';

import { useControlledValue } from '../../../../hooks/useControlledValue/useControlledValue';
import { BreakpointSwitch } from '../../../breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { Button } from '../../../button/components/Button/Button';
import { IconButton } from '../../../button/components/IconButton/IconButton';
import { Typography } from '../../../typography/components/Typography/Typography';

interface Props {
   total: number;
   current?: number;
   defaultCurrent?: number;
   truncate?: boolean;
   onChange?: (page: number) => void;
   renderItem?: (page: number, children: ReactNode) => ReactNode;
}

const createPanes = (panes: 5 | 7, total: number, current: number, truncate: boolean): Array<number | string> => {
   // 1 - 2 - 3 - 4 <-- total = 4
   if (total <= panes) {
      return Array.from(Array(total), (_, page) => page + 1);
   }

   // 3 - 4 - 5 - 6 - 7 - 8 - 9 <-- total = 20
   if (!truncate) {
      let startingPage = current - Math.floor(panes / 2);
      if (startingPage < 1) startingPage = 1;
      if (startingPage + panes > total) startingPage = total - panes + 1;

      return Array.from(Array(panes), (_, page) => startingPage + page);
   }

   const startCollapseOffset = Math.ceil(panes / 2);
   const endCollapseOffset = total - Math.ceil((panes - 2) / 2);

   // 1 - 2 - 3 - 4 - 5 - ... - 10
   if (current <= startCollapseOffset && current < endCollapseOffset) {
      return [...Array.from(Array(panes - 2), (_, page) => page + 1), '...', total];
   }

   // 1 - ... - 6 - 7 - 8 - 9 - 10
   if (current >= endCollapseOffset && current > startCollapseOffset) {
      return [1, '...', ...Array.from(Array(panes - 2), (_, page) => page + total - panes + 3)];
   }

   // 1 - ... - 4 - 5 - 6 - ... - 10
   const numberOfMiddlePages = panes - 4;
   const startingPage = current - Math.floor(numberOfMiddlePages / 2);

   return [1, '...', ...Array.from(Array(numberOfMiddlePages), (_, i) => startingPage + i), '...', total];
};

/* istanbul ignore next */
const getSafeNewPage = (unsafePage: number, total: number) => {
   let newPage = unsafePage;
   if (newPage < 1) newPage = 1;
   else if (newPage > total) newPage = total;
   return newPage;
};

export const Pagination: FC<Props> = ({ total, current: currentPageFromProps, defaultCurrent = 1, truncate = true, onChange, renderItem }) => {
   const [current, setCurrent] = useControlledValue(currentPageFromProps, onChange, defaultCurrent);

   /* istanbul ignore next */
   const handlePageClick = (page: number) => {
      const newPage = getSafeNewPage(page, total);
      if (current !== newPage) setCurrent(newPage);
   };

   const phoneArray = useMemo<Array<string | number>>(() => createPanes(5, total, current, truncate), [total, current, truncate]);
   const paneArray = useMemo<Array<string | number>>(() => createPanes(7, total, current, truncate), [total, current, truncate]);

   const renderPageButton = (pane: number, button: ReactNode) => (renderItem ? renderItem(pane, button) : button);

   return (
      <nav aria-label="pagination navigation" data-testid="pagination" className="flex items-center">
         <IconButton icon="chevron-left" ariaLabel="Go to previous page" disabled={current <= 1} onClick={() => handlePageClick(current - 1)} attributes={{ 'data-testid': 'pagination-previous' }} />
         <BreakpointSwitch includeViews={['phone']}>
            {phoneArray.map((pane, index) =>
               pane === '...' ? (
                  <div
                     key={`pagination-pane--truncate--${index === 1 ? 'start' : 'end'}`}
                     data-testid={`pagination-pane--mobile--truncate--${index === 1 ? 'start' : 'end'}`}
                     className="text-component-navigation-pagination-item-label-color-text-inactive w-component-navigation-pagination-item-touch-area-sizing-size min-h-component-navigation-pagination-item-touch-area-sizing-min-size min-w-component-navigation-pagination-item-touch-area-sizing-min-size flex items-center justify-center"
                  >
                     <Typography variant="body-highlight" color="inherit">
                        ...
                     </Typography>
                  </div>
               ) : (
                  <div
                     key={`pagination-pane--mobile--${pane}`}
                     data-testid={`pagination-pane--mobile--${pane}`}
                     className={classNames(
                        'min-h-component-navigation-pagination-item-touch-area-sizing-min-size min-w-component-navigation-pagination-item-touch-area-sizing-min-size p-component-navigation-pagination-item-touch-area-spacing-padding group flex cursor-pointer items-center justify-center',
                        { 'pointer-events-none': current === (pane as number) }
                     )}
                  >
                     {renderPageButton(
                        pane as number,
                        <Button variant={current === (pane as number) ? 'primary' : 'tertiary'} ariaLabel={current === (pane as number) ? `Page ${pane}` : `Go to page ${pane}`} onClick={() => handlePageClick(pane as number)}>
                           <Typography variant={current === (pane as number) ? 'body-highlight' : 'body'} color={current === (pane as number) ? 'inherit' : 'strong'}>
                              {pane}
                           </Typography>
                        </Button>
                     )}
                  </div>
               )
            )}
         </BreakpointSwitch>
         <BreakpointSwitch excludeViews={['phone']}>
            {paneArray.map((pane, index) =>
               pane === '...' ? (
                  <div
                     key={`pagination-pane--truncate--${index === 1 ? 'start' : 'end'}`}
                     data-testid={`pagination-pane--truncate--${index === 1 ? 'start' : 'end'}`}
                     className="text-component-navigation-pagination-item-label-color-text-inactive w-component-navigation-pagination-item-text-area-sizing-width p-component-navigation-pagination-item-container-spacing-padding box-content flex aspect-square items-center justify-center"
                  >
                     <Typography variant="body-highlight" color="inherit">
                        ...
                     </Typography>
                  </div>
               ) : (
                  <div
                     key={`pagination-pane--${pane}`}
                     data-testid={`pagination-pane--${pane}`}
                     className={classNames(
                        'min-h-component-navigation-pagination-item-touch-area-sizing-min-size min-w-component-navigation-pagination-item-touch-area-sizing-min-size p-component-navigation-pagination-item-touch-area-spacing-padding group flex cursor-pointer items-center justify-center',
                        { 'pointer-events-none': current === (pane as number) }
                     )}
                  >
                     {renderPageButton(
                        pane as number,
                        <Button variant={current === (pane as number) ? 'primary' : 'tertiary'} ariaLabel={current === (pane as number) ? `Page ${pane}` : `Go to page ${pane}`} onClick={() => handlePageClick(pane as number)}>
                           <Typography variant={current === (pane as number) ? 'body-highlight' : 'body'} color={current === (pane as number) ? 'inherit' : 'strong'}>
                              {pane}
                           </Typography>
                        </Button>
                     )}
                  </div>
               )
            )}
         </BreakpointSwitch>
         <IconButton icon="chevron-right" ariaLabel="Go to next page" disabled={current >= total} onClick={() => handlePageClick(current + 1)} attributes={{ 'data-testid': 'pagination-next' }} />
      </nav>
   );
};
